import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Form, Dropdown, Table, Pagination } from "react-bootstrap";
import { Common, EmployeeIcons } from "../../assets/images";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  setActualRevenueSheet,
  setShowActualRevenueSheetExportDropdown,
  setActualRevenueSheetPageNo,
  setActualRevenueSheetPageSize,
  setActualRevenueDate,
  setProjectedSalarySheet,
  setProjectedType,
  setActualTypeRevenue,
  setActualSalarySheet,
  setActualType,
} from "../../store/slices/projectSlice";
import SelectSearch from "react-select-search";
import { Timesheet } from "../../interfaces/timesheet";
import Content from "./get-actual-revenue-sheet";
import { exportToCsv, exportToPDF } from "../../config";
import DatePicker from "react-datepicker";
import { useReactToPrint } from "react-to-print";
import { ToastErrorComponent } from "../Toasts";

interface DayAccumulator {
  [key: string]: string | number;
}

const ActualRevenueSheet = () => {
  const dispatch = useAppDispatch();
  const projectState = useAppSelector((state) => state.project);
  const [show, setShow] = useState(false);
  const [errorContent, setErrorContent] = useState("");
  const state = useAppSelector((state) => state.employee);
  const getlastDayOfMonth = () => {
    const actualSheetDate = new Date(projectState.actualRevenueDate);

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    const actualYear = actualSheetDate.getFullYear();
    const actualMonth = actualSheetDate.getMonth();

    if (actualYear === currentYear && actualMonth === currentMonth) {
      // If the actualSheetDate is within the current month, return the current date
      return new Date();
    } else {
      // If the actualSheetDate is not in the current month, calculate the last date of the month
      return new Date(actualYear, actualMonth + 1, 0);
    }
  };
  const currentDate = getlastDayOfMonth();
  const monthYearString = new Date(
    projectState.actualRevenueDate,
  ).toLocaleString("default", {
    month: "long",
    year: "numeric",
  });
  const revenueSheetRef = useRef(null);
  const [print, setPrint] = useState(false);
  const handlePrint = useReactToPrint({
    content: () => revenueSheetRef.current,
    onAfterPrint: () => setPrint(false),
  });
  const handlePrinted = async () => {
    await setPrint(true);
    handlePrint();
  };
  const profileData = useAppSelector((state) => state.settings.profileData);
  // Pagination && Filter
  const [filteredData, setFilteredData] = useState<Timesheet[]>([]);
  const totalPages = Math.ceil(
    projectState.actualRevenueSheetTotalRecords /
      projectState.actualRevenueSheetPageSize,
  );
  const totalCount = projectState.actualRevenueSheetTotalRecords;

  // show 5 pages at a time in pagination bar and rest will be shown in next and previous
  const showPages = 5;
  const startPage =
    Math.floor((projectState.actualRevenueSheetPageNo - 1) / showPages) *
    showPages;
  const endPage = startPage + showPages;
  const pages = [...Array(totalPages).keys()].slice(startPage, endPage);
  // handle go to page number that is after the 4 pages of current page
  const handleGoToNextPages = () => {
    //   if it is in the last 5 pages then dont do anything
    if (projectState.actualRevenueSheetPageNo > totalPages - showPages) {
      return;
    }
    dispatch(
      setActualRevenueSheetPageNo(
        projectState.actualRevenueSheetPageNo + showPages,
      ),
    );
  };

  const handleGoToPreviousPages = () => {
    //   if it is in the first 5 pages then dont do anything
    if (projectState.actualRevenueSheetPageNo <= 5) {
      return;
    }
    dispatch(
      setActualRevenueSheetPageNo(
        projectState.actualRevenueSheetPageNo - showPages,
      ),
    );
  };

  const handlePageChange = (page: any) => {
    dispatch(setActualRevenueSheetPageNo(page));
  };

  const handlePageSizeChange = (event: any) => {
    const selectedPageSize = parseInt(event.target.value, 10); // Parse the value to a number
    dispatch(setActualRevenueSheetPageSize(selectedPageSize));
  };
  useEffect(() => {
    setFilteredData(projectState.actualRevenue);
  }, [projectState.actualRevenue]);

  const generateDaysOfWeek = () => {
    const daysInMonth = currentDate.getDate();
    const daysOfWeek = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day,
      );
      const dayOfMonth = date.getDate();
      const dayOfWeek = date.toLocaleString("default", { weekday: "short" });

      daysOfWeek.push(
        <React.Fragment key={day}>
          {dayOfMonth}
          <br />
          {dayOfWeek}
        </React.Fragment>,
      );
    }
    return daysOfWeek;
  };

  const tableData = {
    tableHeading: [
      "Sr. No.",
      "Emp Code",
      "Emp Name",
      "Category",
      "Category Price",
      "Project Id.",
      ...generateDaysOfWeek(),
      "Hours",
      "Hours Cost",
      "Overtime",
      "Overtime Cost",
      "Gross Revenue",
    ],
    tableData: filteredData,
  };
  const projectOptions = projectState.projectIds.map((item) => ({
    name: item.projectId + " - " + item.projectName,
    value: item.projectId,
  }));

  const twoWords = (name: string) => {
    const words = name.split(" ");
    return words[0] + " " + words[1];
  };

  // Calculate current month days
  const d = projectState.actualRevenueDate
    ? new Date(projectState.actualRevenueDate)
    : new Date();
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const totalDaysInMonth = new Date(year, month, 0).getDate();

  const basicPriceTimelineFunction = (index: number) => {
    const mEmployee = state.allEmployeesList.find(
      (item) => item.empId === index,
    );
    const timeLine = mEmployee
      ? mEmployee.transferTimeline.find((item) => {
          const fromDate = new Date(item.fromDate);
          const toDate = new Date(item.toDate);
          const currentDate = new Date(projectState.actualRevenueDate);
          currentDate.setHours(0, 0, 0, 0);
          fromDate.setHours(0, 0, 0, 0);
          toDate.setHours(0, 0, 0, 0);
          return (
            new Date(currentDate) >= new Date(fromDate) &&
            new Date(currentDate) <= new Date(toDate)
          );
        })
      : null;
    if (timeLine) {
      return timeLine.price;
    } else {
      return mEmployee ? mEmployee.empProjectPrice : 0;
    }
  };

  const workingHoursTimelineFunction = (index: number) => {
    const mEmployee = state.allEmployeesList.find(
      (item) => item.empId === index,
    );
    const timeLine = mEmployee
      ? mEmployee.transferTimeline.find((item) => {
          const fromDate = new Date(item.fromDate);
          const toDate = new Date(item.toDate);
          const currentDate = new Date(projectState.actualRevenueDate);
          currentDate.setHours(0, 0, 0, 0);
          fromDate.setHours(0, 0, 0, 0);
          toDate.setHours(0, 0, 0, 0);
          return (
            new Date(currentDate) >= new Date(fromDate) &&
            new Date(currentDate) <= new Date(toDate)
          );
        })
      : null;
    if (timeLine) {
      return timeLine.workingHours;
    } else {
      return mEmployee ? mEmployee.empProjectWorkingHours : 0;
    }
  };

  const overTimePriceTimelineFunction = (index: number) => {
    const mEmployee = state.allEmployeesList.find(
      (item) => item.empId === index,
    );
    const timeLine = mEmployee
      ? mEmployee.transferTimeline.find((item) => {
          const fromDate = new Date(item.fromDate);
          const toDate = new Date(item.toDate);
          const currentDate = new Date(projectState.actualRevenueDate);
          currentDate.setHours(0, 0, 0, 0);
          fromDate.setHours(0, 0, 0, 0);
          toDate.setHours(0, 0, 0, 0);
          return (
            new Date(currentDate) >= new Date(fromDate) &&
            new Date(currentDate) <= new Date(toDate)
          );
        })
      : null;
    if (timeLine) {
      return timeLine.overTimePrice;
    } else {
      return mEmployee ? mEmployee.overTimePrice : 0;
    }
  };

  const projectIdTimelineFunction = (index: number) => {
    const mEmployee = state.allEmployeesList.find(
      (item) => item.empId === index,
    );
    const timeLine = mEmployee
      ? mEmployee.transferTimeline.find((item) => {
          const fromDate = new Date(item.fromDate);
          const toDate = new Date(item.toDate);
          const currentDate = new Date(projectState.actualRevenueDate);
          currentDate.setHours(0, 0, 0, 0);
          fromDate.setHours(0, 0, 0, 0);
          toDate.setHours(0, 0, 0, 0);
          return (
            new Date(currentDate) >= new Date(fromDate) &&
            new Date(currentDate) <= new Date(toDate)
          );
        })
      : null;
    if (timeLine) {
      const project = projectState.projectIds.find(
        (item) => item.projectId === timeLine.projectId,
      );
      return project?.projectName;
    } else {
      const project = projectState.projectIds.find(
        (item) => item.projectId === mEmployee?.empProjectId,
      );
      return mEmployee ? project?.projectName : "";
    }
  };

  const categoryTimelineFunction = (index: number) => {
    const mEmployee = state.allEmployeesList.find(
      (item) => item.empId === index,
    );
    const timeLine = mEmployee
      ? mEmployee.transferTimeline.find((item) => {
          const fromDate = new Date(item.fromDate);
          const toDate = new Date(item.toDate);
          const currentDate = new Date(projectState.actualRevenueDate);
          currentDate.setHours(0, 0, 0, 0);
          fromDate.setHours(0, 0, 0, 0);
          toDate.setHours(0, 0, 0, 0);
          return (
            new Date(currentDate) >= new Date(fromDate) &&
            new Date(currentDate) <= new Date(toDate)
          );
        })
      : null;
    if (timeLine) {
      return timeLine.category;
    } else {
      return mEmployee ? mEmployee.empProjectCategory : "";
    }
  };

  const renderTableData = () => {
    const daysInMonth = currentDate.getDate();
    const daysOfMonth = Array.from(
      { length: daysInMonth },
      (_, index) => index + 1,
    );
    return tableData.tableData.map((item, index) => {
      const rowData = daysOfMonth.map((dayOfMonth, dayIndex) => {
        const attendance = item.attendances.find(
          (attendance) => new Date(attendance.date).getDate() === dayOfMonth,
        );
        const date = attendance
          ? new Date(attendance.date).toLocaleString("default", {
              weekday: "short",
            })
          : "";
        const fridayHours = attendance
          ? attendance.actualHours - workingHoursTimelineFunction(item.empId)
          : "-";
        // If friday hours is 0, then show '-'
        const fridayHoursZero = fridayHours === 0 ? "-" : fridayHours;
        const hours = attendance
          ? date === "Fri"
            ? fridayHoursZero
            : attendance.actualHours
          : "-"; // Default to 8 if date is missing
        return <td key={dayIndex}>{hours}</td>;
      });

      const totalHours = item.attendances.reduce((acc, curr) => {
        return acc + curr.actualHours;
      }, 0);

      const totalOverTime = item.attendances.reduce((acc, curr) => {
        return acc + curr.actualOverTime;
      }, 0);

      const actualTotalHours = totalHours - totalOverTime;

      // get sum of todaysSalar from attendances
      // get sum of todaysSalar from attendances
      const salaryCalculation = (hours: number) => {
        const salary =
          basicPriceTimelineFunction(item.empId) > 0
            ? (basicPriceTimelineFunction(item.empId) /
                totalDaysInMonth /
                workingHoursTimelineFunction(item.empId)) *
              hours
            : 0;
        return salary;
      };

      const overTimePrice = overTimePriceTimelineFunction(item.empId)
        ? overTimePriceTimelineFunction(item.empId) > 0
          ? totalOverTime * overTimePriceTimelineFunction(item.empId)
          : salaryCalculation(totalOverTime)
        : salaryCalculation(totalOverTime);

      const grossRevenue = salaryCalculation(actualTotalHours) + overTimePrice;
      return (
        <tr key={index} style={{ borderColor: "rgba(32, 32, 32, 0.20)" }}>
          <td>
            {totalCount > 0
              ? (projectState.actualRevenueSheetPageNo - 1) *
                  projectState.actualRevenueSheetPageSize +
                index +
                1
              : index + 1}
          </td>
          <td>{item.empId}</td>
          <td>{print ? twoWords(item.empName) : item.empName}</td>
          <td>{categoryTimelineFunction(item.empId)}</td>
          <td>{basicPriceTimelineFunction(item.empId)}</td>
          <td>{projectIdTimelineFunction(item.empId)}</td>
          {rowData}
          {/* Add Total working hours of an employee */}
          <td>{actualTotalHours}</td>
          <td>{salaryCalculation(actualTotalHours).toFixed(2)}</td>
          <td>{totalOverTime}</td>
          <td>{overTimePrice}</td>
          <td>{grossRevenue.toFixed(2)}</td>
        </tr>
      );
    });
  };

  const tableFooterData = () => {
    // Sum of all employess working hours of all days
    const totalHoursWorked = tableData.tableData.reduce((total, employee) => {
      // Iterate through each attendance record of the employee and sum up the working hours
      const employeeHours = employee.attendances.reduce((acc, attendance) => {
        return acc + attendance.actualHours;
      }, 0);
      // Add the total working hours of the employee to the total
      return total + employeeHours;
    }, 0);

    const totalOverTime = tableData.tableData.reduce((total, employee) => {
      // Iterate through each attendance record of the employee and sum up the working hours
      const overTimeHours = employee.attendances.reduce((acc, attendance) => {
        return acc + attendance.actualOverTime;
      }, 0);
      // Add the total working hours of the employee to the total
      return total + overTimeHours;
    }, 0);

    const totalActualHoursSalary = tableData.tableData.reduce(
      (total, employee) => {
        const employeeHours = employee.attendances.reduce((acc, attendance) => {
          return acc + attendance.actualHours;
        }, 0);
        const overTimeHours = employee.attendances.reduce((acc, attendance) => {
          return acc + attendance.actualOverTime;
        }, 0);
        const actualTotalHours = employeeHours - overTimeHours;
        const salary =
          basicPriceTimelineFunction(employee.empId) > 0
            ? (basicPriceTimelineFunction(employee.empId) /
                totalDaysInMonth /
                workingHoursTimelineFunction(employee.empId)) *
              actualTotalHours
            : 0;
        return total + salary;
      },
      0,
    );

    const totalOverTimeSalary = tableData.tableData.reduce(
      (total, employee) => {
        const overTimeHours = employee.attendances.reduce((acc, attendance) => {
          return acc + attendance.actualOverTime;
        }, 0);
        const salaryBasic =
          basicPriceTimelineFunction(employee.empId) > 0
            ? (basicPriceTimelineFunction(employee.empId) /
                totalDaysInMonth /
                workingHoursTimelineFunction(employee.empId)) *
              overTimeHours
            : 0;
        const salryOvertime =
          overTimePriceTimelineFunction(employee.empId) > 0
            ? overTimePriceTimelineFunction(employee.empId) * overTimeHours
            : salaryBasic;
        return total + salryOvertime;
      },
      0,
    );

    const totalGrossRevenue = totalActualHoursSalary + totalOverTimeSalary;

    return (
      <tr>
        <td colSpan={6} className="text-end">
          Total
        </td>
        {/* Sum of all employees working hours of one day */}
        {generateDaysOfWeek().map((item, index) => (
          <td key={index}>
            {tableData.tableData.reduce((acc, curr) => {
              const attendance = curr.attendances.find(
                (attendance) =>
                  new Date(attendance.date).getDate() === index + 1,
              );
              return acc + (attendance ? attendance.actualHours : 0);
            }, 0)}
          </td>
        ))}
        <td>{totalHoursWorked - totalOverTime}</td>
        <td>{totalActualHoursSalary.toFixed(2)}</td>
        <td>{totalOverTime}</td>
        <td>{totalOverTimeSalary.toFixed(2)}</td>
        <td>{totalGrossRevenue.toFixed(2)}</td>
      </tr>
    );
  };

  const daysInMonth = currentDate.getDate();
  const daysOfMonth = Array.from(
    { length: daysInMonth },
    (_, index) => index + 1,
  );

  const csvData = tableData.tableData.map((item, index) => {
    const dayAccumulator: DayAccumulator = {};

    const rowData = daysOfMonth.map((dayOfMonth, dayIndex) => {
      const attendance = item.attendances.find(
        (attendance) => new Date(attendance.date).getDate() === dayOfMonth,
      );

      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        dayOfMonth,
      );
      const dayOfWeek = date.toLocaleString("default", { weekday: "short" });

      const hours = attendance ? attendance.actualHours : "-"; // Default to 8 if date is missing
      dayAccumulator[`${dayOfMonth + " " + dayOfWeek} `] = hours;
      return hours;
    });

    const totalHours = item.attendances.reduce((acc, curr) => {
      return acc + curr.actualHours;
    }, 0);

    const totalOverTime = item.attendances.reduce((acc, curr) => {
      return acc + curr.actualOverTime;
    }, 0);

    const actualTotalHours = totalHours - totalOverTime;

    // get sum of todaysSalar from attendances
    // get sum of todaysSalar from attendances
    const salaryCalculation = (hours: number) => {
      const salary =
        basicPriceTimelineFunction(item.empId) > 0
          ? (basicPriceTimelineFunction(item.empId) /
              totalDaysInMonth /
              workingHoursTimelineFunction(item.empId)) *
            hours
          : 0;
      return salary;
    };

    const overTimePrice =
      overTimePriceTimelineFunction(item.empId) > 0
        ? totalOverTime * overTimePriceTimelineFunction(item.empId)
        : salaryCalculation(totalOverTime);

    const grossRevenue = salaryCalculation(actualTotalHours) + overTimePrice;

    return {
      "Sr. No.": index + 1,
      "Emp Code": item.empId,
      "Emp Name": item.empName,
      Category: item.empProjectCategory,
      "Category Price": item.empProjectPrice,
      "Project Id.": item.empProjectId,
      // Place dayAccumulator here between "Project Id." and "Total Hours"
      ...dayAccumulator,
      Hours: actualTotalHours,
      "Hours Cost": salaryCalculation(actualTotalHours),
      Overtime: totalOverTime,
      "Overtime Cost": overTimePrice,
      "Gross Revenue": grossRevenue.toFixed(2),
    };
  });

  const handleProjectId = (value: any) => {
    if (projectState.actualRevenueSheet.includes(value)) {
      window.scrollTo(0, 0);
      setErrorContent("Project ID already selected");
      setShow(true);
      return;
    }
    dispatch(
      setActualRevenueSheet([...projectState.actualRevenueSheet, value]),
    );
    dispatch(setActualTypeRevenue("Project"));
  };

  return (
    <div className=" mt-2 section px-4 py-4">
      <Content />
      <ToastErrorComponent
        show={show}
        setShow={setShow}
        content={errorContent}
      />
      {profileData.permissions.showPettyCash ? (
        <Row>
          <Col sm="12">
            <div className="section-heading mb-4">
              <h1 className="heading mb-0">
                Actual Revenue Sheet for the month of {monthYearString}
              </h1>
              <div className="d-flex align-items-center justify-content-between mt-4">
                <div className="w-25">
                  <Form.Group controlId="formBasicEmail">
                    <h4
                      className="sub-heading normal-font-weight textBlack"
                      style={{ fontSize: "18px" }}
                    >
                      Project ID
                    </h4>
                    <Form.Group className="position-relative input-design d-flex align-items-center mt-2">
                      <SelectSearch
                        name="emp"
                        options={projectOptions}
                        value={projectState.actualRevenueSheet}
                        onChange={(value: any) => handleProjectId(value)}
                        search={true}
                        placeholder="Select Project Id"
                      />
                      <img src={Common.ArrowDown} alt="message" />
                    </Form.Group>
                  </Form.Group>
                </div>
                <div className="d-flex align-items-center gap-4">
                  <Dropdown
                    show={projectState.showActualRevenueSheetExportDropdown}
                    className="position-relative"
                  >
                    <button
                      id="dropdown-basic"
                      onClick={() => {
                        dispatch(
                          setShowActualRevenueSheetExportDropdown(
                            !projectState.showActualRevenueSheetExportDropdown,
                          ),
                        );
                      }}
                      className=" bg-transparent dropdown-toggle-white"
                    >
                      Export{" "}
                      <img
                        src={EmployeeIcons.ArrowDownBlue}
                        className="ms-2"
                        alt="down-arrow"
                      />
                    </button>
                    <Dropdown.Menu
                      className="mt-1 custom-scrollbar"
                      show={projectState.showActualRevenueSheetExportDropdown}
                    >
                      <Dropdown.Item
                        onClick={() => {
                          exportToCsv(csvData, "actual-revenue-sheet");
                          dispatch(
                            setShowActualRevenueSheetExportDropdown(false),
                          );
                        }}
                      >
                        CSV
                      </Dropdown.Item>
                      <Dropdown.Item onClick={handlePrinted}>PDF</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            </div>
          </Col>
          <Col sm="3">
            <Form.Group className="mt-4" controlId="formBasicEmail">
              <h4
                className="sub-heading normal-font-weight textBlack"
                style={{ fontSize: "18px" }}
              >
                Select Month
              </h4>
              <Form.Group className="d-flex align-items-center input-design position-relative w-100">
                <DatePicker
                  name="month"
                  selected={
                    projectState.actualRevenueDate
                      ? new Date(projectState.actualRevenueDate)
                      : null
                  }
                  onChange={(e: any) =>
                    dispatch(setActualRevenueDate(e.toLocaleDateString()))
                  }
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  autoComplete="off"
                  placeholderText="Select start date"
                  className="w-100 custom-input"
                  dateFormat="MMMM"
                />
                <img
                  src={Common.Calendar}
                  className="calendar-date-recur"
                  alt="calendarblack"
                />
              </Form.Group>
            </Form.Group>
          </Col>
          {projectState.actualTypeRevenue === "" ? null : (
            <Col sm="12">
              <Form.Group
                className="mt-5 d-flex align-items-center gap-4"
                controlId="formBasicEmail"
              >
                <h4
                  className="sub-heading normal-font-weight textBlack mb-0"
                  style={{ fontSize: "18px" }}
                >
                  Selected {projectState.actualTypeRevenue}:
                </h4>

                <div className="d-flex align-items-center gap-2 overflow-auto">
                  {projectState.actualRevenueSheet.map((item, index) => (
                    <div
                      key={index}
                      className="selected-projects primary-button d-flex align-items-center gap-3 py-0 px-4"
                      style={{
                        height: "40px",
                        borderRadius: "20px",
                        boxShadow: "none",
                      }}
                    >
                      <h4 className="mb-0" style={{ fontSize: "16px" }}>
                        {item}
                      </h4>
                      <button
                        className="btn p-0 border-0"
                        onClick={() => {
                          const newProjects =
                            projectState.actualRevenueSheet.filter(
                              (project) => project !== item,
                            );
                          dispatch(setActualRevenueSheet(newProjects));
                          if (projectState.actualRevenueSheet.length === 1) {
                            dispatch(setActualTypeRevenue(""));
                          }
                        }}
                      >
                        <img
                          src={Common.Cross}
                          style={{ width: "22px", height: "22px" }}
                          alt="close"
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </Form.Group>
            </Col>
          )}
          <Col sm="12">
            <div className="p-3" ref={revenueSheetRef}>
              {print && (
                <>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <img src={Common.Vision} width="110px" alt="logo" />
                    </div>
                    <div className="letter-logo text-center gap-2">
                      <img src={Common.LogoSVG} width="80px" alt="logo" />
                      <div className="mt-1">
                        <h3
                          className="logo-text mb-0"
                          style={{
                            color: "#202020",
                            fontSize: "10px",
                            fontWeight: "700",
                          }}
                        >
                          Senyar Arabian Trading Co. LTD
                        </h3>
                        <h3
                          className="logo-text logo-arabic mb-0"
                          style={{
                            color: "#202020",
                            fontSize: "13px",
                            fontWeight: "700",
                          }}
                        >
                          شركة سنيار العربية التجارية المحدودة
                        </h3>
                      </div>
                    </div>
                  </div>
                  <h1 className="sub-heading text-center">
                    Actual Revenue Sheet
                  </h1>
                  <h1 className="sub-heading text-center mt-0">
                    {monthYearString}
                  </h1>
                </>
              )}
              <Table
                striped
                bordered
                responsive
                className={
                  print ? "mt-4 text-center print-sheet" : "mt-2 text-center"
                }
              >
                <thead>
                  <tr className="border-0">
                    {tableData.tableHeading.map((item, index) => (
                      <th className="border-0" key={index}>
                        {item}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>{renderTableData()}</tbody>
                <tfoot>{tableFooterData()}</tfoot>
              </Table>
            </div>
          </Col>
          {filteredData.length > 0 && (
            <Col sm="12">
              <div className="mt-3 d-flex justify-content-between align-items-center">
                <Form.Group
                  className="d-flex align-items-center gap-2"
                  controlId="formBasicEmail"
                >
                  <h4
                    className="sub-heading normal-font-weight textBlack"
                    style={{ fontSize: "14px", width: "230px" }}
                  >
                    Entries per page
                  </h4>
                  <Form.Select
                    name="pageSize"
                    onChange={handlePageSizeChange}
                    value={projectState.actualRevenueSheetPageSize.toString()}
                    className="custom-input border-fill custom-height"
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="100">100</option>
                    <option value="10000">All</option>
                  </Form.Select>
                </Form.Group>
                <div className="pagination-custom">
                  <Pagination className="justify-content-end">
                    <Pagination.Item onClick={() => handleGoToPreviousPages()}>
                      <img src={Common.DoubleArrowLeft} alt="arrow" />
                    </Pagination.Item>
                    <Pagination.Item
                      onClick={() =>
                        handlePageChange(
                          projectState.actualRevenueSheetPageNo > 1
                            ? projectState.actualRevenueSheetPageNo - 1
                            : 1,
                        )
                      }
                    >
                      <img
                        src={EmployeeIcons.ArrowDownBlue}
                        className="rotate-left"
                        alt="arrow"
                      />
                    </Pagination.Item>

                    {pages.map((page, index) => (
                      <Pagination.Item
                        key={index}
                        onClick={() => handlePageChange(page + 1)}
                        className={
                          page + 1 === projectState.actualRevenueSheetPageNo
                            ? "active-page"
                            : ""
                        }
                      >
                        {page + 1}
                      </Pagination.Item>
                    ))}

                    <Pagination.Item
                      onClick={() =>
                        handlePageChange(
                          projectState.actualRevenueSheetPageNo < totalPages
                            ? projectState.actualRevenueSheetPageNo + 1
                            : totalPages,
                        )
                      }
                    >
                      <img
                        src={EmployeeIcons.ArrowDownBlue}
                        className="rotate-right"
                        alt="arrow"
                      />
                    </Pagination.Item>
                    <Pagination.Item
                      onClick={() => {
                        handleGoToNextPages();
                      }}
                    >
                      <img src={Common.DoubleArrowRight} alt="arrow" />
                    </Pagination.Item>
                  </Pagination>
                </div>
              </div>
            </Col>
          )}
        </Row>
      ) : (
        <div className="text-center mt-5">
          <h1 className="heading">Unauthorized Access</h1>
        </div>
      )}
    </div>
  );
};
export default ActualRevenueSheet;
